import { ViewCarousel as ViewCarouselIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { ImageCarouselType } from '../../../models/ContentManagementInterface';
import CropAdjustDialog from './CropAdjustDialog';
import CropReviewDialog from './CropReviewDialog';
import MultiCardOfferDialog from './MultiCardOfferDialog';
import PasteReplaceDialog from './PasteReplaceDialog';
import { ImageCardsSplitState } from './useImageCardsSplit';

const SPLIT_LABEL = 'Split a screenshot into cards';

type CarouselProps = ImageCarouselType['properties'];

/** The post-split "these crops came from the screenshot" hint + Adjust action. */
function CroppedNote({
  canAdjust,
  onAdjust,
  onDismiss,
}: {
  canAdjust: boolean;
  onAdjust: () => void;
  onDismiss: () => void;
}) {
  return (
    <Alert
      severity="info"
      onClose={onDismiss}
      sx={{ py: 0 }}
      action={
        canAdjust ? (
          <Button color="inherit" size="small" onClick={onAdjust}>
            Adjust crops
          </Button>
        ) : undefined
      }
    >
      Cropped from the screenshot — adjust the framing, or drop a sharper original onto a card to
      replace it.
    </Alert>
  );
}

/**
 * The Image Cards block's screenshot-splitting UI: the explicit
 * "Split a screenshot into cards" button, the auto-detect offer dialog, the
 * Replace confirm, and the post-split low-resolution note. All flow state lives
 * in `useImageCardsSplit` (owned by the editor so the paste `onImage` can reach it).
 */
export default function ImageCardsFromScreenshot({
  split,
}: {
  split: ImageCardsSplitState<CarouselProps>;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            split.busy ? <CircularProgress size={14} /> : <ViewCarouselIcon fontSize="small" />
          }
          onClick={split.start}
          disabled={split.busy}
        >
          {SPLIT_LABEL}
        </Button>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Paste or pick a screenshot or PDF of several captioned photos.
        </Typography>
      </Box>

      {split.error && (
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          {split.error}
        </Typography>
      )}

      {split.showNote && (
        <CroppedNote
          canAdjust={split.canAdjust}
          onAdjust={split.openAdjust}
          onDismiss={split.dismissNote}
        />
      )}

      <MultiCardOfferDialog
        open={split.offerOpen}
        cardCount={split.offerCount}
        onSplit={split.acceptOffer}
        onAddOne={split.declineOffer}
        onCancel={split.cancelOffer}
      />

      <PasteReplaceDialog
        open={split.pending !== null}
        onConfirm={split.confirmPending}
        onCancel={split.cancelPending}
      />

      <CropReviewDialog
        open={split.reviewOpen}
        properties={split.review}
        onUse={split.applyReview}
        onAdjust={split.openAdjust}
        onCancel={split.cancelReview}
      />

      <CropAdjustDialog
        key={split.adjustSource?.path ?? 'none'}
        open={split.adjustOpen}
        source={split.adjustSource}
        initialBoxes={split.adjustBoxes}
        headings={split.adjustHeadings}
        busy={split.busy}
        onApply={split.applyAdjust}
        onCancel={split.cancelAdjust}
      />
    </Box>
  );
}
